import { useFeatureFlag } from '@user-portal/experimentation';

import { getAppropriateDiscountForSubscription } from 'store/subscriptions/helpers';
import { selectDiscountedSubscriptions } from 'store/subscriptions/selectors';
import { selectCurrentUser } from 'store/user/selectors';

import { CURRENCY_SIGN_MAP } from 'constants/currency';
import { DATE_FORMAT } from 'constants/date';
import { PRODUCT_CODES, SPECIAL_OFFERS } from 'constants/product';
import { DISCOUNT_TYPES } from 'constants/subscriptions';

import { useSelector } from 'hooks';

import { getFormattedDate, hasTimePassedSince } from 'helpers/date';
import { getPriceFromCents } from 'helpers/prices';
import { isMainSubscription } from 'helpers/subscriptions';
import { isEmpty, replaceUnderscoreToDash } from 'helpers/utils';

import { IDiscountsForSubscriptions } from 'types/subscription';

import LogoIcon from 'assets/images/logo.svg';
import VipSupportIcon from 'assets/img/svg/vip-support-ico.svg';

import { SECONDS_TO_SHOW_UPDATING_STATUS, SUBSCRIPTION_PAUSE_PERIOD, SUBSCRIPTION_STATES } from './constants';
import { getSubscriptionName, getUpcomingProduct, isNextChargeAvailable } from './helpers';
import normalizeDateByLocale from './helpers/normalizeDateByLocale';
import { ISubscriptionCard } from './types';

const SINGLE_BILLING_CYCLE = 1;

const useSubscriptionCard = ({
    onCancelSubscription,
    onReturnOffer,
    onRestoreSubscription,
    onResumeSubscription,
    subscription,
    onPauseSubscription,
}: ISubscriptionCard) => {
    const {
        fee,
        product,
        state,
        discounts,
        is_trial: isTrial,
        is_upgrade: isUpgrade,
        external_id: externalId,
        expired_date: expiredDate,
        pause: { pause_to, pause_from },
        cancelled_at: cancelledAt,
        product_code: productCode,
        product_type: productCodeType,
        internal_cancel_at: internalCancelAt,
        product_description: productDescription,
        subscription_period: subscriptionPeriod,
        subscription_trial_period: subscriptionTrialPeriod,
        billing_cycles: billingCycles,
        is_promo_code_used: isPromoCodeUsed,
    } = subscription;

    const { price: fullPrice, currency, trial_price: trialPrice } = product ?? {};
    const country = useSelector(selectCurrentUser).country;

    const isCancelledSubscription = state === SUBSCRIPTION_STATES.CANCELLED || Boolean(cancelledAt);
    const isSubscriptionActive = state === SUBSCRIPTION_STATES.ACTIVE;
    const isSubscriptionRedemption = state === SUBSCRIPTION_STATES.REDEMPTION;
    const isSubscriptionPaused = state === SUBSCRIPTION_STATES.PAUSED;
    const isSubscriptionUpdating = state === SUBSCRIPTION_STATES.PENDING;
    const canPauseSubscription = !discounts[SPECIAL_OFFERS.PAUSE_SUBSCRIPTION];

    const discountedSubscriptions: IDiscountsForSubscriptions = useSelector(selectDiscountedSubscriptions);

    const isDiscountOfferApplied = Boolean(subscription?.discount?.discount_granted_at);
    const isReturnOfferApplied = Boolean(subscription?.discount.discount_type === DISCOUNT_TYPES.extra);

    const isExpiredSubscription = isCancelledSubscription && !isSubscriptionActive;
    const isExpiresSubscription = internalCancelAt && isSubscriptionActive;

    const isOfferAvailableForSubscription = !isEmpty(
        getAppropriateDiscountForSubscription(subscription, discountedSubscriptions, DISCOUNT_TYPES.extra)
    );

    const isDiscountedSubscription = isReturnOfferApplied || isDiscountOfferApplied;

    const expirationDate = getFormattedDate(expiredDate, DATE_FORMAT);
    const cancelledAtDate = getFormattedDate(cancelledAt, DATE_FORMAT);
    const pausedTo = pause_to ? getFormattedDate(pause_to, DATE_FORMAT) : '';

    const isTimePassedSincePaused = Boolean(
        pause_from && hasTimePassedSince(pause_from, 'seconds', SECONDS_TO_SHOW_UPDATING_STATUS)
    );
    const isReminderSet = subscription.reminder;
    const subscriptionPausePeriod = SUBSCRIPTION_PAUSE_PERIOD;
    const isVIPSupport = productCode === PRODUCT_CODES.APP_VIP_SUPPORT;
    const tabIcon = isVIPSupport ? VipSupportIcon : LogoIcon;

    const { value: isDynamicProductDescriptionsEnabled } = useFeatureFlag<boolean>({
        type: 'boolean',
        flagKey: 'dynamic_product_descriptions',
        defaultValue: false,
    });

    const heading = replaceUnderscoreToDash(
        getSubscriptionName(
            productCodeType,
            isUpgrade,
            isVIPSupport,
            isDynamicProductDescriptionsEnabled ? productDescription : undefined
        )
    );
    const price = isTrial ? trialPrice : fullPrice;
    const currencySign = CURRENCY_SIGN_MAP[currency] || currency;

    const feeData = fee?.fee_enabled
        ? {
              fee_amount: `${getPriceFromCents(fee.fee_amount, currency)} ${currency}`,
              fee_total_price: `${getPriceFromCents(fee.fee_total_price, currency)} ${currency}`,
              fee_expired_at: normalizeDateByLocale(fee.fee_expired_at, country),
              fee_period: fee.fee_period ? fee.fee_period * subscriptionPeriod : 365,
          }
        : null;

    return {
        feeData,
        price: `${price} ${currencySign}`,
        state,
        tabIcon,
        heading,
        externalId,
        upcomingProduct: getUpcomingProduct(subscription),
        isSubscriptionRedemption,
        isExpiresSubscription,
        isExpiredSubscription,
        isSubscriptionPaused,
        isSubscriptionUpdating,
        isReturnOfferApplied,
        isDiscountedSubscription,
        isReminderSet,
        isTimePassedSincePaused,
        cancelledAtDate,
        pausedTo,
        handleCancelSubscription: () => {
            onCancelSubscription(subscription);
        },
        isTrial,
        expirationDate: replaceUnderscoreToDash(cancelledAt ? cancelledAtDate : expirationDate),
        subscriptionPeriod: isTrial ? subscriptionTrialPeriod! : subscriptionPeriod,
        isMainSubscription: isMainSubscription(productCodeType),
        productCode,
        subscriptionPausePeriod,
        handleResumeSubscription: () => {
            onResumeSubscription(subscription);
        },
        onShowReturnOffer: () => {
            onReturnOffer(subscription);
        },
        handleRestoreSubscription: () => {
            onRestoreSubscription(subscription);
        },
        isOfferAvailableForSubscription,
        isUpgrade,
        isNextChargeAvailable: isNextChargeAvailable(billingCycles, isTrial),
        isOneTimeProduct: billingCycles === SINGLE_BILLING_CYCLE && !isTrial,
        isPromoCodeUsed,
        canPauseSubscription,
        handlePauseSubscription: (isPauseFlow?: boolean) => {
            onPauseSubscription(subscription, isPauseFlow);
        },
    };
};

export default useSubscriptionCard;
